import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import {
  getUserProfile,
  mergeAuthUserAfterProfileSave,
  profileUserFromApiResponse,
  updateMyProfile,
} from "../api/profile";

const PROFILE_URL = {
  instagram: (name) => `https://instagram.com/${name}`,
  youtube: (name) => `https://youtube.com/@${name}`,
  tiktok: (name) => `https://tiktok.com/@${name}`,
  facebook: (name) => `https://facebook.com/${name}`,
};

/**
 * The signed-in user's social accounts: what's linked, and linking or removing one.
 * `stateOf` says what stands between them and submitting on a platform.
 */
export default function useSocialAccounts(setToast) {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const userId = user?.id || user?._id;

  const refresh = async () => {
    if (!userId) return;
    try {
      const response = await getUserProfile(userId);
      const next = profileUserFromApiResponse(response, userId);
      if (next) setUser(next);
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    refreshRef.current();
  }, [userId]);

  const socials = user?.socials || user?.profile?.socials || {};
  const linkOf = (id) => String(socials[id] || "").trim();
  const instagramVerified = Boolean(user?.instagramVerification?.verified);
  const stateOf = (id) => {
    if (!linkOf(id)) return "missing";
    return id === "instagram" && !instagramVerified ? "unverified" : "ready";
  };

  /** Saves a profile link or username; an empty value disconnects. Resolves true once saved. */
  const saveLink = async (platform, value) => {
    setSaving(true);
    try {
      let url = String(value || "").trim();
      if (url && !url.startsWith("http")) {
        url = PROFILE_URL[platform.id](url.replace(/^@/, ""));
      }
      const next = { ...user.profile?.socials, [platform.id]: url };
      const saved = await updateMyProfile({ socials: next });
      setUser((prev) => mergeAuthUserAfterProfileSave(prev, saved, next));
      await refresh();
      setToast?.(
        url
          ? t("toasts.platformConnectedSuccess", { platform: platform.name })
          : t("campaignFlow.disconnected", { platform: platform.name })
      );
      return true;
    } catch (error) {
      console.error("Error updating platform:", error);
      setToast?.(error.response?.data?.message || t("toasts.failedUpdatePlatform"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { linkOf, stateOf, saveLink, saving, refresh };
}
